/**
 * Fondo fotográfico dinámico del hero del login (Claude Design · Login.dc.html).
 *
 * Reemplaza el documento ilustrado (factura de muestra) por un carrusel de
 * fotografías de Panamá con crossfade + Ken Burns lento. Es puramente
 * decorativo: las imágenes viven en /public/images/login-bg y no requieren
 * ninguna llamada a la API.
 */
import { useEffect, useState } from 'react';

interface Slide {
  src: string;
  caption: string;
}

const SLIDES: Slide[] = [
  { src: '/images/login-bg/panama-skyline-noche.webp', caption: 'Ciudad de Panamá' },
  { src: '/images/login-bg/canal-panama-esclusas.webp', caption: 'Canal de Panamá' },
  { src: '/images/login-bg/casco-viejo-noche.webp', caption: 'Casco Antiguo' },
  { src: '/images/login-bg/catedral-casco-viejo.webp', caption: 'Catedral de Panamá' },
  { src: '/images/login-bg/canal-panama-atardecer.webp', caption: 'Canal de Panamá' },
  { src: '/images/login-bg/canal-esclusas-amanecer.webp', caption: 'Canal de Panamá' },
];

const SLIDE_DURATION_MS = 7000;

export function LoginBackgroundSlideshow() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {SLIDES.map((slide, i) => (
        <img
          key={slide.src}
          src={slide.src}
          alt=""
          loading={i === 0 ? 'eager' : 'lazy'}
          className={`login-slide absolute inset-0 h-full w-full object-cover ${
            i % 2 === 0 ? 'login-slide-pan-a' : 'login-slide-pan-b'
          } ${i === index ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}

      {/* Scrim de marca: mismo tono navy que antes, ahora sobre foto en vez
          de gradiente plano. Sostiene el contraste del texto blanco y del
          logo sin importar qué tan clara sea la fotografía activa. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 900px 700px at 15% 0%, rgba(20,59,86,.5) 0%, rgba(20,59,86,0) 55%), linear-gradient(180deg, rgba(13,42,61,.82) 0%, rgba(6,26,38,.92) 100%)',
        }}
      />

      {/* Leyenda + indicadores del carrusel. Oculto en el hero compacto de
          mobile (hero apilado, poca altura disponible) — ahí la foto se ve
          igual, solo sin esta leyenda flotante. */}
      <div className="absolute inset-x-6 bottom-16 z-[2] hidden items-center justify-between gap-3 sm:inset-x-10 sm:bottom-20 sm:flex lg:inset-x-[clamp(32px,4.5vw,64px)]">
        <span className="login-caption font-mono text-[11px] uppercase tracking-[0.22em] text-white/45" key={SLIDES[index].caption + index}>
          {SLIDES[index].caption}
        </span>
        <div className="flex gap-1.5">
          {SLIDES.map((slide, i) => (
            <span
              key={slide.src}
              className={`h-1 rounded-full transition-all duration-700 ${
                i === index ? 'w-5 bg-teal-400' : 'w-1 bg-white/25'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
